import ReglasReservaFields from './ReglasReservaFields';
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { PlanDuracionConfig, updatePlanDuracionConfig } from '../../services/planconfigService';
import { fetchPlanes, Plan } from '../../services/planService';

interface Props {
    isOpen: boolean;
    config: PlanDuracionConfig | null;
    onClose: () => void;
    onUpdated: (updatedConfig: PlanDuracionConfig) => void;
}

const EditPlanConfigModal: React.FC<Props> = ({ isOpen, config, onClose, onUpdated }) => {
    const [reglas, setReglas] = useState<Record<string,any>>({});
    const [planes, setPlanes] = useState<Plan[]>([]);
    const [planId, setPlanId] = useState<number | ''>('');
    const [unidad, setUnidad] = useState<'hora' | 'dia'>('hora');
    const [dias, setDias] = useState<number | ''>('');
    const [horas, setHoras] = useState<number | ''>('');
    const [usaTemporizador, setUsaTemporizador] = useState<boolean>(false);
    const [descripcion, setDescripcion] = useState<string>('');
    const [estado, setEstado] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const cargarPlanes = async () => {
            try {
                const data = await fetchPlanes();
                // Filtrar solo planes activos
                setPlanes(data.filter((p) => p.estado));
            } catch (err) {
                /* Browser diagnostics omitted. */
            }
        };
        if (isOpen) cargarPlanes();
    }, [isOpen]);

    useEffect(() => {
        if (config) {
            setReglas(config.reglas_reserva||{});
            setPlanId(config.plan);
            setUnidad(config.unidad);
            setDias(config.dias ?? '');
            setHoras(config.horas ?? '');
            setUsaTemporizador(config.usa_temporizador);
            setDescripcion(config.descripcion || '');
            setEstado(config.estado);
            setError(null);
        }
    }, [config]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!config) return;
        if (!planId) {
            setError('Debe seleccionar un plan');
            return;
        }
        if ((unidad === 'dia' && (dias === '' || dias <= 0)) || (unidad === 'hora' && (horas === '' || horas <= 0))) {
            setError('Ingrese una duración positiva para la unidad seleccionada');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const updated = await updatePlanDuracionConfig(config.id, {
                plan: planId,
                unidad,
                dias: dias === '' ? undefined : Number(dias),
                horas: horas === '' ? undefined : Number(horas),
                reglas_reserva: reglas,
                usa_temporizador: usaTemporizador,
                descripcion: descripcion || undefined,
                estado,
            });
            onUpdated(updated);
            onClose();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.message || 'Error desconocido');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !config) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white max-h-[90vh] overflow-y-auto p-6 rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-xl relative">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Editar Configuración de Plan</h3>
                    <div className="flex items-center gap-4">
                        {error && <div className="text-red-500 text-sm">{error}</div>}
                        <button 
                            onClick={onClose} 
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
<ReglasReservaFields value={reglas} onChange={setReglas}/>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Plan
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={planId}
                                onChange={(e) => setPlanId(Number(e.target.value))}
                            >
                                <option value="">-- Seleccione un plan --</option>
                                {planes.map((p) => (
                                    <option key={p.PlanID} value={p.PlanID}>
                                        {p.NombrePlan}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Unidad
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={unidad}
                                onChange={(e) => setUnidad(e.target.value as 'hora' | 'dia')}
                            >
                                <option value="hora">Hora</option>
                                <option value="dia">Día</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Días
                            </label>
                            <input
                                type="number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={dias}
                                onChange={(e) => setDias(e.target.value === '' ? '' : Number(e.target.value))}
                                min={0}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Horas
                            </label>
                            <input
                                type="number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={horas}
                                onChange={(e) => setHoras(e.target.value === '' ? '' : Number(e.target.value))}
                                min={0}
                            />
                        </div>
                    </div>

                    {/* Campo usa_temporizador */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                                <span className="text-sm font-medium text-gray-700">
                                    Usar Temporizador
                                </span>
                                <p className="text-xs text-gray-500 mt-1">
                                    Si está activo, la hora de finalización se determina al terminar el uso
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={usaTemporizador}
                                    onChange={(e) => setUsaTemporizador(e.target.checked)}
                                />
                                <div
                                    className={`w-10 h-6 rounded-full transition-colors ${
                                        usaTemporizador ? 'bg-[#c1348b]' : 'bg-gray-300'
                                    }`}
                                >
                                    <div
                                        className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${
                                            usaTemporizador ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                    ></div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Descripción
                        </label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            rows={3}
                        />
                    </div>

                    <div className="mb-4">
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                                <span className="text-sm font-medium text-gray-700">
                                    Estado de la Configuración
                                </span>
                                <p className="text-xs text-gray-500 mt-1">
                                    {estado ? 'Activo' : 'Inactivo'}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={estado}
                                    onChange={(e) => setEstado(e.target.checked)}
                                />
                                <div
                                    className={`w-10 h-6 rounded-full transition-colors ${estado ? 'bg-[#c1348b]' : 'bg-gray-300'}`}
                                >
                                    <div
                                        className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${estado ? 'translate-x-5' : 'translate-x-0'}`}
                                    ></div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg transition-colors font-medium flex items-center"
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                'Actualizar Cambios'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditPlanConfigModal;


